import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseUri = process.env.REACT_APP_STRAPI_URL;

// Define a service using a base URL and expected endpoints
export const contentApi = createApi({
    reducerPath: 'contentApi',
    baseQuery: fetchBaseQuery({
        baseUrl: baseUri,
        prepareHeaders: (headers, { getState }) => {
            // todo get auth token from state
            //const token = (getState() as RootState).auth.token;
            const token = process.env.REACT_APP_STRAPI_TOKEN;
            // If we have a token set in state, let's assume that we should be passing it.
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    endpoints: (builder) => ({
        loginContent: builder.query<LoginContent, void>({
            query: () => ({
                url: 'api/login?populate=*',
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),
        invoiceContent: builder.query<InvoiceContent, void>({
            query: () => ({
                url: 'api/invoice?populate=*',
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),
        globalContent: builder.query<GlobalContent, void>({
            query: () => ({
                url: 'api/global?populate=*',
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),

        historyState: builder.query<HistoryState, void>({
            query: () => ({
                url: 'api/historystate?populate[hs][populate]=%2A',
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useLoginContentQuery, useGlobalContentQuery, useInvoiceContentQuery, useHistoryStateQuery } = contentApi;

export interface LoginContent {
    data: ContentData<LoginContentDataAttributes>;
}
export interface InvoiceContent {
    data: ContentData<InvoiceContentDataAttributes>;
}
export interface GlobalContent {
    data: ContentData<GlobalContentDataAttributes>;
}
export interface HistoryState {
    data: ContentData<HistoryStateDataAttributes>;
}
export interface HistoryStateDataAttributes {
    hs: Array<HistoryStatDynamicDataAttributes>;
}
export interface HistoryStatDynamic {
    data: Array<HistoryStatDynamicDataAttributes>;
}
export interface HistoryStatDynamicDataAttributes {
    status: Status;
    stateDescription: string;
    __component: string;
    icon_list_status: SingleMedia;
    icon_main_status: SingleMedia;
    documentType: DocumentTypes;
}
export interface DocumentTypes {
    DocumentTypes: string;
}
export interface ContentData<T = any> {
    id: string;
    attributes: T;
}

export interface LoginContentDataAttributes {
    ocr_field: string;
    zipcode_field: string;
    log_in_btn: string;
    missing_invoice: string;
    generic_error: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    bankid_header: string;
    bankid_help_text: string;
    bankid_or_text: string;
    bankid_button_text: string;
    bg_img: SingleMedia;
    header: string;
    bankid_ununathorized:string;
    retry_button_text:string;
}

export interface InvoiceContentDataAttributes {
    info_header: string;
    info_sub_header: string;
    history_header: string;
    history_sub_header: string;
    recipient: string;
    issuer: string;
    payment_info: string;
    invoice_number: string;
    ocr: string;
    invoice_date: string;
    due_date: string;
    payment_number_type: string;
    payment_number_value: string;
    log_out_btn: string;
    download_btn: string;
    remaining_amount: string;
    no_document: string;
}
export interface GlobalContentDataAttributes {
    fg_logo: MultiMedia;
    arrow: SingleMedia;
    download: SingleMedia;
    copy: SingleMedia;
    plus: SingleMedia;
    minus: SingleMedia;
}

export interface Status {
    data: ContentData<StatusDataAttributes>;
}
export interface StatusDataAttributes {
    skynet_status: string;
    status: string;
}

export interface SingleMedia {
    data: MediaData;
}

export interface MultiMedia {
    data: Array<MediaData>;
}

export interface MediaData {
    id: string;
    attributes: MediaAttributes;
}

export interface MediaAttributes {
    name: string;
    alternativeText: string;
    caption: string;
    width: number;
    height: number;
    formats: any;
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl: string;
    provider: string;
    provider_metadata: string;
    createdAt: string;
    updatedAt: string;
}

export interface ResolvedImage {
    url: string | null;
    exists: boolean;
    title: string | undefined;
}

export const ResolveImageUrl = (media: SingleMedia | MediaData[] | undefined, imageVariant? : number): ResolvedImage => {
    let singleMedia = (media as SingleMedia)?.data;
    if (singleMedia) {
        // join base url with media url and strip the first slash if it exists
        let url = (media as SingleMedia).data.attributes.url.replace(/^\//, '')
        return {
            url: url,
            exists: url?.length > 0,
            title: (media as SingleMedia).data.attributes.alternativeText,
        };
    }
    let mediaData = (media as MediaData[])?.[imageVariant ?? 0].attributes;
    if (mediaData) {
      // join base url with media url and strip the first slash if it exists
      let url = (media as MediaData[])?.[imageVariant ?? 0].attributes?.url.replace(/^\//, '');
      return {
          url: url,
          exists: url?.length > 0,
          title: (media as MediaData[])?.[imageVariant ?? 0].attributes.alternativeText,
      };
  }
    return { url: null, exists: false, title: undefined };
};
